/**
 * @file CustomReportSelect.tsx
 * @module app/custom/Select/CustomReportSelect
 * @description Renders the CustomReportSelect reusable select control, including option presentation, creation, multi-selection, and table-filter behavior.
 *
 * @author Rocket Technologies Ltd
 * @copyright 2026 Rocket Technologies Ltd. All rights reserved.
 * @since 1.1.1
 * @version 1.3.0
 */

/* eslint-disable jsx-a11y/anchor-is-valid */
import { useState } from 'react';
import Select, { components } from 'react-select';
const CustomReportSelect = (props: any) => {
  const InputOption = ({
    getStyles,
    Icon,
    isDisabled,
    isFocused,
    isSelected,
    children,
    innerProps,
    ...rest
  }: any) => {
    const [isActive, setIsActive] = useState(false);
    const onMouseDown = () => setIsActive(true);
    const onMouseUp = () => setIsActive(true);
    const onMouseLeave = () => setIsActive(true);
    // prop assignment
    const props = {
      ...innerProps,
      onMouseDown,
      onMouseUp,
      onMouseLeave,
    };
    return (
      <components.Option
        {...rest}
        isDisabled={isDisabled}
        isFocused={isFocused}
        isSelected={isSelected}
        getStyles={getStyles}
        innerProps={props}
      >
        <div className="form-check form-check-custom form-check-solid form-check-lg">
          <input
            className="form-check-input me-4"
            type="checkbox"
            value=""
            checked={isSelected}
          />
          {children}
          {/* <input type="checkbox" checked={isSelected} /> */}
        </div>
      </components.Option>
    );
  };
  const customFilterOption = (option: any, rawInput: any) => {
    // If a custom filter is passed from parent (for server-side search etc.), use that
    if (props.filterOption) {
      return props.filterOption(option, rawInput);
    }
    // Default behaviour: filter by "title" field on the option's data
    const label = option?.data?.title || '';
    const input = (rawInput || '').toLowerCase();
    const text = label.toLowerCase();
    // When input is empty, keep all options
    if (!input) {
      return true;
    }
    return text.includes(input);
  };
  return (
    <>
      <Select
        closeMenuOnSelect={props.closeMenuOnSelect}
        isLoading = {props.isLoading}
        onMenuClose={props.onMenuClose}
        onMenuOpen={props.onMenuOpen}
        hideSelectedOptions={props.hideSelectedOptions}
        defaultValue={props.defaultValue}
        value={props.value}
        options={props.options}
        onInputChange={props.onInputChange}
        onMenuScrollToBottom={props.onMenuScrollToBottom}
        isSearchable={props.isSearchable}
        getOptionLabel={props.getOptionLabel}
        filterOption={customFilterOption}
        styles={{
          option: (base:any) => ({
            ...base,
            border: `0.5px solid #f9f9f9`,
            margin: '0px',
            background: 'white',
            padding: '1rem 1.25rem',
            ':active': {
              ...base[':active'],
              color: '#1b74e4',
              background: '#f1faff',
            },
            ':hover': {
              ...base[':active'],
              color: '#1b74e4',
              background: '#f1faff',
            },
            color: '#5e6278',
            fontSize: '16px',
            fontWeight: '600',
          }),
          control: (base:any) => ({
            ...base,
            border: props.border
              ? '0.5px solid ' + props.border
              : '0.5px solid #e0e0df',
            background: '#ffff',
            boxShadow: 'none',
            minHeight: '40px',
            borderRadius: '8px',
            padding: '6.5px 20.4px 6.5px 10px',
            fontSize: '16px',
            ':hover': {
              ...base[':hover'],
              border: props.border
                ? '0.5px solid ' + props.border
                : '0.5px solid #e0e0df',
            },
            fontWeight: '600',
          }),
          multiValue: (base:any) => {
            return {
              ...base,
              fontSize: '16px',
              fontWeight: '600',
              borderRadius: '6px',
              backgroundColor: '#e7f1fd',
              padding: '8px 8px 8px 10px',
              color: '#1a1a1a',
            };
          },
          menu: (base:any) => ({
            ...base,
            // width: '300px',
          }),
          indicatorSeparator: (base:any) => ({
            ...base,
            background: '#f9f9f9',
          }),
          indicatorsContainer: (base:any) => ({
            ...base,
            color: '#f9f9f9',
            strokeWidth: '5px',
          }),
          multiValueRemove: (base:any, { data }:any) => ({
            ...base,
            fontSize: '16px',
            fontWeight: '600',
            svg: {
              width: '20px',
              height: '20px',
              color: '#7c7c7c',
            },
            ':hover': {
              backgroundColor: '#e7f1fd',
            },
          }),
        }}
        onChange={props.onChange}
        // menuIsOpen={true}
        isDisabled={props.isDisabled ? props.isDisabled : false}
        isMulti={props.isMulti}
        components={{
          Option: InputOption,
          MultiValueContainer: ({ selectProps, data }:any) => {
            const values = selectProps.value;
            if (values) {
              let name_ =
                values[values.length - 1].id === data.id
                  ? values.length === 1
                    ? data.label
                    : data.name
                  : data.name + ', ';
              return values[values.length - 1].id === data.id ? (
                values.length === 1 ? (
                  name_
                ) : (
                  ''
                )
              ) : (
                <div className="d-flex align-items-center">
                  {values[values.length - values.length].id === data.id ? (
                    <>
                      <div className="fs-16 fw-600 text-dark me-2">
                        {props.isDeduct ? values.length - 1 : values.length}
                      </div>
                      <h6 className="fs-16 fw-600 text-black mb-0">
                        {props.text}
                      </h6>
                    </>
                  ) : (
                    <></>
                  )}
                </div>
              );
            } else return '';
          },
        }}
      />
    </>
  );
};
export { CustomReportSelect };
